import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Key, Upload, Trash2, FileText, Tag } from 'lucide-react';
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'

const ApiDocumentation = () => {
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">API Documentation</h1>
        <p className="text-lg text-gray-600">
          Complete reference for Simple File Upload's REST API
        </p>
      </div>

      {/* Authentication Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Authentication
          </CardTitle>
          <CardDescription>
            How to authenticate your API requests
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
              <div className="text-sm text-blue-700">
                All API requests must be authenticated using HTTP Basic Authentication over HTTPS.
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-2">Getting Started</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
              <li>Generate a new API key in your account settings</li>
              <li>Use your API key as the username and secret as the password</li>
              <li>Include credentials in the Authorization header of your requests</li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium mb-2">Example Request</h4>
            <SyntaxHighlighter
              language="bash"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
{`wget -qO /dev/stdout \\
  --user "your_api_key_here" \\
  --password "your_secret_here" \\
  https://app.simplefileupload.com/api/v1/files`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      {/* File Operations Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            File Operations
          </CardTitle>
          <CardDescription>
            Upload, retrieve, and delete files
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="upload" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="upload">Upload Files</TabsTrigger>
              <TabsTrigger value="delete">Delete Files</TabsTrigger>
              <TabsTrigger value="list">List Files</TabsTrigger>
            </TabsList>
            
            {/* Upload Tab */}
            <TabsContent value="upload" className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="default">POST</Badge>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/file</code>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Basic Upload</h4>
                <SyntaxHighlighter
                  language="bash"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`curl https://app.simplefileupload.com/api/v1/file \\
  -F "file=@/path/to/your/image.jpg" \\
  -u YOUR_API_KEY:YOUR_SECRET`}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="font-medium mb-2">Upload with Tag</h4>
                <SyntaxHighlighter
                  language="bash"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`curl https://app.simplefileupload.com/api/v1/file \\
  -F "file=@/path/to/your/image.jpg" \\
  -F "tag=profile-photos" \\
  -u YOUR_API_KEY:YOUR_SECRET`}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="font-medium mb-2">Response Format</h4>
                <SyntaxHighlighter
                  language="json"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`{
  "data": {
    "id": "56",
    "type": "files",
    "attributes": {
      "cdn-url": "https://subdomain.files-simplefileupload.com/...",
      "file-size": 9182,
      "filename": "image.jpg",
      "created-at": "2023-01-30",
      "tag": {
        "id": 2,
        "name": "profile-photos"
      }
    }
  }
}`}
                </SyntaxHighlighter>
              </div>
            </TabsContent>

            {/* Delete Tab */}
            <TabsContent value="delete" className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
                  <div className="text-sm text-red-700">
                    Deletion is immediate and permanent. Files cannot be recovered once deleted.
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Delete by URL</h4>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="destructive">DELETE</Badge>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/file?url={'{file_url}'}</code>
                </div>
                <SyntaxHighlighter
                  language="bash"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`wget -qO /dev/stdout \\
  --user "YOUR_API_KEY" \\
  --password "YOUR_SECRET" \\
  --method=DELETE \\
  "https://app.simplefileupload.com/api/v1/file?url=https://subdomain.files-simplefileupload.com/..."`}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="font-medium mb-2">Delete by ID</h4>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="destructive">DELETE</Badge>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/files/:id</code>
                </div>
                <SyntaxHighlighter
                  language="bash"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`curl -X DELETE \\
  https://app.simplefileupload.com/api/v1/files/56 \\
  -u YOUR_API_KEY:YOUR_SECRET`}
                </SyntaxHighlighter>
              </div>
            </TabsContent>

            {/* List Tab */}
            <TabsContent value="list" className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="secondary">GET</Badge>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/files</code>
              </div>

              <div>
                <h4 className="font-medium mb-2">Request</h4>
                <SyntaxHighlighter
                  language="bash"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`curl https://app.simplefileupload.com/api/v1/files \\
  -u YOUR_API_KEY:YOUR_SECRET`}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="font-medium mb-2">Response Format</h4>
                <SyntaxHighlighter
                  language="json"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`{
  "data": [
    {
      "id": "56",
      "type": "files",
      "attributes": {
        "cdn-url": "https://subdomain.files-simplefileupload.com/...",
        "file-size": 1078900,
        "filename": "paul-gilmore-DgRbZSWej-o-unsplash",
        "created-at": "2023-01-30",
        "tag": {
          "id": null,
          "name": null
        }
      }
    }
  ]
}`}
                </SyntaxHighlighter>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Tags Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Tag Operations
          </CardTitle>
          <CardDescription>
            Organize files with tags
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">List All Tags</h4>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary">GET</Badge>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/tags</code>
            </div>
            <SyntaxHighlighter
              language="bash"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
{`curl https://app.simplefileupload.com/api/v1/tags \\
  -u YOUR_API_KEY:YOUR_SECRET`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h4 className="font-medium mb-2">Get Tag Details</h4>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary">GET</Badge>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/tags/:id</code>
            </div>
            <SyntaxHighlighter
              language="bash"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
{`curl https://app.simplefileupload.com/api/v1/tags/1 \\
  -u YOUR_API_KEY:YOUR_SECRET`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h4 className="font-medium mb-2">Tag Response Format</h4>
            <SyntaxHighlighter
              language="json"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
{`{
  "data": {
    "id": "16",
    "type": "tags",
    "attributes": {
      "name": "website-files"
    },
    "relationships": {
      "files": {
        "data": [
          {
            "id": "97",
            "type": "files"
          },
          {
            "id": "98",
            "type": "files"
          }
        ]
      }
    }
  }
}`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Reference</CardTitle>
          <CardDescription>
            All API endpoints at a glance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-4 text-sm font-medium border-b pb-2">
              <div>Method</div>
              <div>Endpoint</div>
              <div>Description</div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm py-2 border-b">
              <Badge variant="default">POST</Badge>
              <code className="text-xs">/api/v1/file</code>
              <span>Upload a file</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm py-2 border-b">
              <Badge variant="destructive">DELETE</Badge>
              <code className="text-xs">/api/v1/file?url=</code>
              <span>Delete file by URL</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm py-2 border-b">
              <Badge variant="destructive">DELETE</Badge>
              <code className="text-xs">/api/v1/files/:id</code>
              <span>Delete file by ID</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm py-2 border-b">
              <Badge variant="secondary">GET</Badge>
              <code className="text-xs">/api/v1/files</code>
              <span>List all files</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm py-2 border-b">
              <Badge variant="secondary">GET</Badge>
              <code className="text-xs">/api/v1/tags</code>
              <span>List all tags</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm py-2">
              <Badge variant="secondary">GET</Badge>
              <code className="text-xs">/api/v1/tags/:id</code>
              <span>Get tag details</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApiDocumentation;
